import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Search, Plus, Download, Mail, Shield, Car } from 'lucide-react';
import { formatDate, formatCurrency, delay } from '../utils/helpers';
import { generateCertificadoPDF } from '../services/pdfService';
import { generateGarantia, generateVehiculo } from '../data/mockData';
import toast from 'react-hot-toast';

const GarantiasListPage = () => {
  const { mockData, user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterNivel, setFilterNivel] = useState('');

  const garantias = React.useMemo(() => {
    let filtered = mockData?.garantias || [];

    if (user?.role === 'vendedor') {
      filtered = filtered.filter(g => g.vendedor === user.email);
    }

    if (searchTerm) {
      filtered = filtered.filter(g =>
        g.vehiculo.matricula.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.propietario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.numeroGarantia.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterEstado) {
      filtered = filtered.filter(g => g.estado === filterEstado);
    }

    if (filterNivel) {
      filtered = filtered.filter(g => g.nivel === parseInt(filterNivel));
    }

    return filtered;
  }, [mockData, searchTerm, filterEstado, filterNivel, user]);

  const columns = [
    {
      header: 'N Garantia',
      accessor: 'numeroGarantia',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold text-gray-900">{row.numeroGarantia}</span>
      )
    },
    {
      header: 'Vehiculo',
      cell: (row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.vehiculo.matricula}</p>
          <p className="text-xs text-gray-500">{row.vehiculo.marca} {row.vehiculo.modelo}</p>
        </div>
      )
    },
    {
      header: 'Propietario',
      accessor: 'propietario.nombre'
    },
    {
      header: 'Nivel',
      cell: (row) => {
        const colors = ['default', 'primary', 'warning', 'success'];
        return <Badge variant={colors[row.nivel - 1]}>{row.nivelNombre}</Badge>;
      }
    },
    {
      header: 'Fecha Alta',
      cell: (row) => <span className="text-sm text-gray-500">{formatDate(row.fechaAlta)}</span>
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.estado === 'activa' ? 'success' : 'default'}>
          {row.estado.toUpperCase()}
        </Badge>
      )
    },
    {
      header: 'Precio',
      cell: (row) => <span className="text-sm font-semibold text-gray-900">{formatCurrency(row.precio)}</span>
    }
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Garantias</h1>
          <p className="text-sm text-gray-500 mt-1">Gestion de garantias de vehiculos</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => navigate('/garantias/nueva')}
        >
          Nueva Garantia
        </Button>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <Input
            placeholder="Buscar por matricula, cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
          <Select
            placeholder="Todos los estados"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            options={[
              { value: 'activa', label: 'Activa' },
              { value: 'finalizada', label: 'Finalizada' }
            ]}
          />
          <Select
            placeholder="Todos los niveles"
            value={filterNivel}
            onChange={(e) => setFilterNivel(e.target.value)}
            options={[
              { value: '1', label: 'Basica' },
              { value: '2', label: 'Estandar' },
              { value: '3', label: 'Premium' },
              { value: '4', label: 'Total' }
            ]}
          />
          <Button variant="outline" fullWidth>
            Exportar CSV
          </Button>
        </div>

        <Table
          columns={columns}
          data={garantias}
          onRowClick={(row) => navigate(`/garantias/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const NuevaGarantiaPage = () => {
  const { mockData, updateMockData, addAuditLog, user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchingVehicle, setSearchingVehicle] = useState(false);

  const [matricula, setMatricula] = useState('');
  const [vehiculo, setVehiculo] = useState(null);
  const [propietario, setPropietario] = useState({
    nombre: '',
    dni: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    codigoPostal: ''
  });
  const [nivel, setNivel] = useState('2');

  const buscarVehiculo = async () => {
    setSearchingVehicle(true);
    await delay(1500);

    const vehiculoMock = generateVehiculo();
    vehiculoMock.matricula = matricula.toUpperCase();
    setVehiculo(vehiculoMock);
    setSearchingVehicle(false);
    toast.success('Vehiculo encontrado');
    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    await delay(1000);

    const nuevaGarantia = {
      ...generateGarantia(),
      vehiculo,
      propietario,
      nivel: parseInt(nivel),
      nivelNombre: ['Basica', 'Estandar', 'Premium', 'Total'][parseInt(nivel) - 1],
      vendedor: user.email,
      fechaAlta: new Date().toISOString()
    };

    const updatedGarantias = [nuevaGarantia, ...mockData.garantias];
    updateMockData({ garantias: updatedGarantias });
    addAuditLog('crear_garantia', 'garantia', nuevaGarantia.id, `Garantia ${nuevaGarantia.numeroGarantia} creada`);
    addNotification('success', 'Garantia creada', `Se ha creado la garantia ${nuevaGarantia.numeroGarantia}`);

    toast.success('Garantia creada exitosamente');
    setLoading(false);

    generateCertificadoPDF(nuevaGarantia);

    setTimeout(() => {
      navigate('/garantias');
    }, 1000);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Nueva Garantia</h1>
        <p className="text-sm text-gray-500 mt-1">Registro rapido de garantia de vehiculo</p>
      </div>

      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-3">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-500 border border-gray-200'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 ${step > s ? 'bg-primary-700' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {step === 1 && (
        <Card title="Paso 1: Buscar Vehiculo">
          <div className="max-w-md mx-auto space-y-4">
            <Input
              label="Matricula"
              value={matricula}
              onChange={(e) => setMatricula(e.target.value)}
              placeholder="1234ABC"
              icon={Car}
              required
            />
            <Button
              variant="primary"
              fullWidth
              onClick={buscarVehiculo}
              loading={searchingVehicle}
              disabled={!matricula}
            >
              Buscar Vehiculo
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && vehiculo && (
        <Card title="Paso 2: Datos del Propietario">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre Completo"
              value={propietario.nombre}
              onChange={(e) => setPropietario({ ...propietario, nombre: e.target.value })}
              required
            />
            <Input
              label="DNI"
              value={propietario.dni}
              onChange={(e) => setPropietario({ ...propietario, dni: e.target.value })}
              required
            />
            <Input
              label="Telefono"
              value={propietario.telefono}
              onChange={(e) => setPropietario({ ...propietario, telefono: e.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              value={propietario.email}
              onChange={(e) => setPropietario({ ...propietario, email: e.target.value })}
              required
            />
            <Input
              label="Direccion"
              value={propietario.direccion}
              onChange={(e) => setPropietario({ ...propietario, direccion: e.target.value })}
              className="md:col-span-2"
            />
            <Input
              label="Ciudad"
              value={propietario.ciudad}
              onChange={(e) => setPropietario({ ...propietario, ciudad: e.target.value })}
            />
            <Input
              label="Codigo Postal"
              value={propietario.codigoPostal}
              onChange={(e) => setPropietario({ ...propietario, codigoPostal: e.target.value })}
            />
          </div>
          <div className="flex justify-between mt-5">
            <Button variant="outline" onClick={() => setStep(1)}>
              Atras
            </Button>
            <Button
              variant="primary"
              onClick={() => setStep(3)}
              disabled={!propietario.nombre || !propietario.dni}
            >
              Siguiente
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card title="Paso 3: Nivel de Cobertura">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            {[
              { nivel: '1', nombre: 'Basica', precio: 299 },
              { nivel: '2', nombre: 'Estandar', precio: 499 },
              { nivel: '3', nombre: 'Premium', precio: 699 },
              { nivel: '4', nombre: 'Total', precio: 899 }
            ].map((option) => (
              <button
                key={option.nivel}
                onClick={() => setNivel(option.nivel)}
                className={`p-5 rounded-lg text-left ${
                  nivel === option.nivel
                    ? 'border-2 border-primary-700 bg-primary-50'
                    : 'border border-gray-200 bg-white'
                }`}
              >
                <Shield className={`w-6 h-6 mb-2 ${nivel === option.nivel ? 'text-primary-700' : 'text-gray-400'}`} />
                <h3 className="font-bold text-sm text-gray-900 mb-1">{option.nombre}</h3>
                <p className="text-lg font-bold text-primary-700">{formatCurrency(option.precio)}</p>
              </button>
            ))}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumen</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Vehiculo:</span>
                <span className="font-semibold text-gray-900">{vehiculo.marca} {vehiculo.modelo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Matricula:</span>
                <span className="font-semibold text-gray-900">{vehiculo.matricula}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Propietario:</span>
                <span className="font-semibold text-gray-900">{propietario.nombre}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Nivel:</span>
                <span className="font-semibold text-gray-900">{['Basica', 'Estandar', 'Premium', 'Total'][parseInt(nivel) - 1]}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              Atras
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              loading={loading}
              icon={Download}
            >
              Generar Certificado
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

const GarantiaDetallePage = () => {
  const { id } = useParams();
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [garantia, setGarantia] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const loadGarantia = async () => {
      await delay(300);
      const found = mockData?.garantias?.find(g => g.id === id);
      setGarantia(found);
      setLoading(false);
    };
    loadGarantia();
  }, [id, mockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-sm text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!garantia) {
    return (
      <div className="space-y-5">
        <div className="text-center py-12">
          <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Garantia no encontrada</h2>
          <p className="text-sm text-gray-500 mb-5">No se pudo encontrar la garantia solicitada</p>
          <Button variant="primary" onClick={() => navigate('/garantias')}>
            Volver al listado
          </Button>
        </div>
      </div>
    );
  }

  const diasRestantes = Math.ceil((new Date(garantia.fechaVencimiento) - new Date()) / (1000 * 60 * 60 * 24));
  const estaProximoVencer = diasRestantes <= 30 && diasRestantes > 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Detalles de Garantia</h1>
          <p className="text-sm text-gray-500 mt-1">Informacion completa de la garantia</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/garantias')}>
          Volver
        </Button>
      </div>

      {estaProximoVencer && (
        <div className="bg-yellow-50 border border-yellow-200 border-l-4 border-l-yellow-400 p-4 rounded-lg">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Garantia proxima a vencer</p>
              <p className="text-xs text-yellow-700">Quedan {diasRestantes} dias</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Card title="Informacion de Garantia">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Numero de Garantia</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.numeroGarantia}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Estado</p>
                <div className="mt-0.5">
                  <Badge variant={garantia.estado === 'activa' ? 'success' : 'default'}>
                    {garantia.estado.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500">Nivel de Cobertura</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.nivelNombre}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Precio</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatCurrency(garantia.precio)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Fecha de Alta</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(garantia.fechaAlta)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Fecha de Vencimiento</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(garantia.fechaVencimiento)}</p>
              </div>
            </div>
          </Card>

          <Card title="Informacion del Vehiculo">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Matricula</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.matricula}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Bastidor</p>
                <p className="text-sm font-semibold text-gray-900 font-mono mt-0.5">{garantia.vehiculo.bastidor}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Marca y Modelo</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.marca} {garantia.vehiculo.modelo}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Version</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.version}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Ano</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.year}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Combustible</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.combustible}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Potencia</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.potencia} CV</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Kilometraje</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.vehiculo.kilometros?.toLocaleString()} km</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Propietario">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.propietario.nombre}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">DNI</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.propietario.dni}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Telefono</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.propietario.telefono}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5 break-all">{garantia.propietario.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Direccion</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.propietario.direccion}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Ciudad</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{garantia.propietario.ciudad} ({garantia.propietario.codigoPostal})</p>
              </div>
            </div>
          </Card>

          <Card title="Acciones">
            <div className="space-y-2">
              <Button
                variant="primary"
                className="w-full"
                icon={Download}
                onClick={() => generateCertificadoPDF(garantia)}
              >
                Descargar Certificado
              </Button>
              <Button
                variant="outline"
                className="w-full"
                icon={Mail}
                onClick={() => toast.success('Email enviado')}
              >
                Enviar por Email
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const GarantiasPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<GarantiasListPage />} />
        <Route path="nueva" element={<NuevaGarantiaPage />} />
        <Route path=":id" element={<GarantiaDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default GarantiasPage;
